import { PieChart } from '@tamagui/lucide-icons';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { QueryState } from '@unistocks/components/query-state/query-state';
import {
  DashboardItemFrame,
  DashboardItemProps
} from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { DashboardItemHeader } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item-header';
import { t } from '@unistocks/localization';
import { PieChartType } from '@unistocks/proxy/axios-client';
import { portfoliosGetQueryKey, usePortfoliosGetQuery } from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { includeAllFilter } from '@unistocks/shared/filter';
import { noop } from '@unistocks/shared/utils/util';
import { Filter } from '@unistocks/types';
import { useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';

const PortfolioPieChart = React.lazy(
  () => import('@unistocks/features/portfolios/components/charts/pie-chart/pie-chart')
);

export const PortfolioOverview: React.FC<DashboardItemProps> = ({
  item,
  openSettings,
  ...frameProps
}) => {
  const { settings } = item;
  const { portfolios, pieChartSettings } = settings;

  const router = useRouter();
  const [filter, setFilter] = useState<Filter<string>>(includeAllFilter as Filter<string>);

  const { data: portfolio, isSuccess } = usePortfoliosGetQuery(portfolios![0]);
  const title = settings.title?.trim() || portfolio?.name;

  const navigateToPortfolio = useCallback(
    () =>
      router.navigate({
        pathname: '/(tabs)/portfolios/[id]',
        params: { id: portfolios![0] }
      }),
    [router, portfolios]
  );

  return (
    <DashboardItemFrame {...frameProps}>
      <DashboardItemHeader
        item={item}
        title={title}
        openSettings={openSettings}
        onPress={navigateToPortfolio}
      />
      <QueryState
        queryKeys={[portfoliosGetQueryKey(portfolios![0])]}
        size={'$5'}
        flex={1}
        py={0}
        renderLoading={<PortfolioOverviewLoader />}
        isEmpty={isSuccess && !portfolio.sections.flatMap(s => s.assets).length}
        noDataIcon={PieChart}
        noDataText={t('::Portfolio:PieChart:NoData')}
      >
        {isSuccess ? (
          <React.Suspense fallback={<PortfolioOverviewLoader />}>
            <PortfolioPieChart
              portfolio={portfolio}
              mode={pieChartSettings?.chartType ?? PieChartType.Categories}
              onModeChange={noop}
              assetFilter={filter}
              setAssetFilter={setFilter}
              sectionFilter={filter}
              setSectionFilter={setFilter}
              showModeSwitch={false}
              flexShrink={1}
              {...{ '$platform-web': { flex: 1 } }}
              chartContainerProps={{ h: '$18', '$platform-web': { flex: 1 } }}
              legendContainerProps={{ maxH: '$10', '$platform-web': { maxH: 'auto' } }}
            />
          </React.Suspense>
        ) : null}
      </QueryState>
    </DashboardItemFrame>
  );
};

const PortfolioOverviewLoader = () => (
  <Loader size={'$5'} textProps={{ size: '$6' }}>
    {t('AbpUi::LoadingWithThreeDot')}
  </Loader>
);
